import { createContext, ReactElement, useContext, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import type { Map as YMap, Doc as YDoc } from 'yjs';
import { createStore, StoreApi, useStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { FrontendSharedEvents } from 'shared/client/consts/frontendSharedEvents';
import { serializeError } from 'shared/client/utils/serializeError';
import { WidgetType } from 'shared/common/utils/widgetsUtils';
import { getContentObjectsMap, getNestedMap, getRootMap, validateYMap } from 'shared/common/utils/yjs';
import { useLearningObjectCommonData } from 'shared/components/providers/LearningObjectCommonDataProvider';
import { logFrontendEventApi } from 'shared/queries/logFrontendEventApi';
import { EvaluationProps } from 'shared/types/evaluationProps';
import { QuestionScoreMode } from 'shared/types/questionScoreMode';
import { useObservedArray, useObservedProperty, UseObservedPropertyOptions } from 'shared/utils/yjs.client';

import { OrchestrationWrapper } from './OrchestrationWrapper';

export type RootMapNames = 'contentObjects' | 'widgetsFormData' | 'sensitiveData';

type ContentObjectStoreType = {
  id: string;
  document: YDoc;
  columnSpan?: number;
};

const ContentObjectStoreContext = createContext<StoreApi<ContentObjectStoreType> | null>(null);

function Fallback() {
  const { t } = useTranslation('widgets');
  return <div>{t('widgets.renderingError')}</div>;
}

type ContentObjectProviderProps = Omit<ContentObjectStoreType, 'elementState' | 'setElementState'> & {
  children: ReactElement;
  withOrchestrationWrapper?: boolean;
};

export function ContentObjectProvider(props: ContentObjectProviderProps) {
  const { id, document, children, columnSpan, withOrchestrationWrapper = true } = props;
  const contentObjectStore = useMemo(
    () =>
      createStore<ContentObjectStoreType, [['zustand/subscribeWithSelector', unknown]]>(
        // @ts-expect-error //TODO - why this error?
        subscribeWithSelector(() => ({
          id,
          document,
          columnSpan
        }))
      ),
    [id, document, columnSpan]
  );

  const { learningObjectDraftId } = useLearningObjectCommonData();

  // There is a general issue where components mounting/dismounting is not being synchronized with how the data updates
  // It's especially prevalent, where a content object renders children that access their own data
  // But the parent content object gets deleted (along with the children data).
  // This can cause the children to still be mounted and try to access their data causing crashes.
  // This is one possible workaround to ensure we do not mount anything that doesn't actually have data in the store
  const contentObjectsMap = getContentObjectsMap(document);
  if (!contentObjectsMap.get(id)) {
    return null;
  }

  const widgetType = contentObjectsMap.get(id)?.get('type') as WidgetType;

  return (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={error => {
        if (learningObjectDraftId) {
          void logFrontendEventApi({
            eventType: FrontendSharedEvents.error.widgetCrash,
            learningObjectDraftId,
            widgetType,
            contentObjectId: id,
            serializedError: serializeError(error),
            url: window.location.href
          });
        }
      }}
    >
      <ContentObjectStoreContext.Provider value={contentObjectStore}>
        {withOrchestrationWrapper ? <OrchestrationWrapper>{children}</OrchestrationWrapper> : children}
      </ContentObjectStoreContext.Provider>
    </ErrorBoundary>
  );
}

export function useContentObject(property: RootMapNames = 'contentObjects') {
  const store = useContext(ContentObjectStoreContext);
  if (store === null) throw new Error('ContentObjectStoreContext not initialized properly.');
  const { document, id, ...rest } = useStore(store);
  const data = validateYMap(getRootMap(document).get(property))?.get(id) as YMap<unknown> | undefined;
  return { data, document, id, ...rest };
}

export function useContentObjectStaticProperty<T = unknown>(
  propertyPath: string,
  parentMap: RootMapNames = 'contentObjects'
) {
  const { data } = useContentObject(parentMap);
  const [observedPropertyName, nestedData] = getNestedMap<T>(propertyPath, data);
  return nestedData?.get(observedPropertyName);
}

export function useContentObjectProperty<T = unknown>(
  propertyPath: string,
  parentMap: RootMapNames = 'contentObjects',
  options: UseObservedPropertyOptions<T> = { deepObserve: false }
) {
  const { data } = useContentObject(parentMap);
  const [observedPropertyName, nestedData] = getNestedMap<T>(propertyPath, data);
  return useObservedProperty<T>(nestedData, observedPropertyName, options);
}

export function useContentObjectArray<T = unknown>(propertyPath: string, parentMap: RootMapNames = 'contentObjects') {
  const { data } = useContentObject(parentMap);
  const [observedArrayName, nestedData] = getNestedMap<T>(propertyPath, data);
  return useObservedArray<T>(nestedData, observedArrayName);
}

export function useContentObjectErrors<SchemaType = Record<string, Array<string>>>() {
  return useContentObjectProperty<Partial<SchemaType>>('errors', 'widgetsFormData');
}

export function useContentObjectScoreWeight() {
  return useContentObjectProperty<number>('scoreWeight', 'widgetsFormData');
}

export function useContentObjectEvaluationProps(): EvaluationProps {
  const [questionScoreMode] = useContentObjectProperty<QuestionScoreMode>('questionScoreMode');
  const [showAnswerButton] = useContentObjectProperty<boolean>('showAnswerButton');
  const [showDetailedFeedback] = useContentObjectProperty<boolean>('showDetailedFeedback');
  const [showHintButton] = useContentObjectProperty<boolean>('showHintButton');
  const [showIndicatorButton] = useContentObjectProperty<boolean>('showIndicatorButton');
  const [showFeedback] = useContentObjectProperty<boolean>('showFeedback');

  return {
    showAnswerButton,
    showDetailedFeedback: showDetailedFeedback ?? false,
    showHintButton,
    showIndicatorButton,
    showFeedback,
    questionScoreMode
  };
}
